import { createContext, createSignal, useContext, type ParentComponent } from 'solid-js';
import { makePersisted } from '@solid-primitives/storage';
import { ru } from './locales/ru';

export type Locale = 'ru' | 'en';

type Dict = typeof ru;

const dictionaries: Record<Locale, Dict> = {
  ru,
  en: ru,
};

type DotPaths<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${P}${K}`
    : T[K] extends Record<string, unknown>
      ? DotPaths<T[K], `${P}${K}.`>
      : never;
}[keyof T & string];

export type TranslationKey = DotPaths<Dict>;

const resolve = (dict: Dict, path: string): string => {
  const segments = path.split('.');
  let node: unknown = dict;
  for (const seg of segments) {
    if (node && typeof node === 'object' && seg in (node as Record<string, unknown>)) {
      node = (node as Record<string, unknown>)[seg];
    } else {
      return path;
    }
  }
  return typeof node === 'string' ? node : path;
};

const interpolate = (template: string, params?: Record<string, string | number>): string => {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    params[key] !== undefined ? String(params[key]) : `{${key}}`,
  );
};

export type Translator = (
  key: TranslationKey | string,
  params?: Record<string, string | number>,
) => string;

interface I18nContextValue {
  locale: () => Locale;
  setLocale: (next: Locale) => void;
  t: Translator;
}

const I18nContext = createContext<I18nContextValue>();

export const I18nProvider: ParentComponent = (props) => {
  const [locale, setLocale] = makePersisted(createSignal<Locale>('ru'), {
    name: 'sportpark.locale',
    storage: localStorage,
  });

  const t: I18nContextValue['t'] = (key, params) => {
    const dict = dictionaries[locale()];
    const template = resolve(dict, key);
    return interpolate(template, params);
  };

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>{props.children}</I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextValue => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within <I18nProvider>');
  return ctx;
};
